import storage from "@provider/storage";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import api, { methods } from "../api";
import type { Account } from "@type/account";
import { useWishlistStore } from "@features/favourite/wishlist-store";
import { useRecipeWishlistStore } from "@features/recipe/favourite/recipe-wishlist-store";
import { setAuthToken } from "@provider/token";

type AuthObject = {
  token: string;
  profile?: Account;
};

type AuthStateType = ReturnType<typeof useAuthValues>;
const AuthStateContext = React.createContext<AuthStateType>(undefined as any);
export const AuthStateProvider = ({ children }: { children: React.ReactNode }) => {
  const values = useAuthValues();
  setAuthToken(values.token);
  return <AuthStateContext.Provider value={values}>{children}</AuthStateContext.Provider>;
};

function useAuthState(): [AuthObject | null, (v: AuthObject | null) => void] {
  const initialState = React.useMemo(() => {
    try {
      const authJSON = storage.getString("auth");
      const auth = JSON.parse(authJSON!);
      return auth as AuthObject;
    } catch {}
    return null;
  }, []);

  const [state, setState] = React.useState(initialState);
  const setInnerState = (newState: AuthObject | null) => {
    setState(newState);
    storage.set("auth", JSON.stringify(newState));
  };
  return [state, setInnerState];
}

function useAuthValues() {
  const [auth, setAuth] = useAuthState();
  const token = auth?.token;
  const enabled = !!token;
  const setInitialWishlist = useWishlistStore((state) => state.initial);
  const setInitialRecipesWishlist = useRecipeWishlistStore((state) => state.initial);

  const {
    data: accountData,
    isLoading,
    isLoadingError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["account", token],
    queryFn: () => {
      return api<Account>({
        path: "/account/profile",
        method: methods.GET,
        token: token!,
      });
    },
    throwOnError: false,
    enabled,
  });

  React.useEffect(() => {
    if (accountData) {
      setAuth({ token: token!, profile: accountData });
      setInitialWishlist(accountData.wishlists);
      setInitialRecipesWishlist(accountData.recipe_wishlists);
    }
  }, [accountData]);

  React.useEffect(() => {
    if (isLoadingError) {
      console.log({ error: "Account loading error" });
      setAuth(null);
    }
  }, [isLoadingError]);

  const login = async (newToken: string) => {
    setAuth({ token: newToken });
  };
  const logout = () => {
    setAuth(null);
  };

  const refetchAccount = React.useCallback(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled]);

  return {
    isRefetching,
    refetch: refetchAccount,
    login,
    logout,
    isLoading: enabled && isLoading,
    isLoadingError: enabled && isLoadingError,
    profile: auth?.profile,
    isAuthenticated: !!auth?.profile && !!token,
    token,
  };
}

export function useAuth() {
  return React.useContext(AuthStateContext);
}
